/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { useState, useCallback } from 'preact/compat';
import { CartItemModel } from '@/quote-management/data/models/negotiable-quote-model';

export interface UseItemsQuotedTemplateReturn {
  dropdownSelections: Record<string, string | undefined>;
  handleItemDropdownChange: (item: CartItemModel, action: string) => void;
  handleDismissRemoveBanner: () => void;
  clearDropdownSelection: (item: CartItemModel) => void;
}

export interface UseItemsQuotedTemplateParams {
  handleRemoveItems: (items: CartItemModel[]) => void;
}

/**
 * Custom hook to manage dropdown selections and item actions for quote template items
 */
export const useItemsQuotedTemplate = (
  params: UseItemsQuotedTemplateParams
): UseItemsQuotedTemplateReturn => {
  const { handleRemoveItems } = params;

  const [dropdownSelections, setDropdownSelections] = useState<
    Record<string, string | undefined>
  >({});

  const handleDismissRemoveBanner = useCallback(() => {
    // The hook manages its own notification state, but we can add custom dismiss logic if needed
  }, []);

  const clearDropdownSelection = useCallback((item: CartItemModel) => {
    const key = item.uid || '';
    setDropdownSelections((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleItemDropdownChange = useCallback(
    (item: CartItemModel, action: string) => {
      const cartItem = item;

      // Handle remove action
      if (action === 'remove') {
        setDropdownSelections((prev) => ({
          ...prev,
          [cartItem.uid || '']: action,
        }));
        handleRemoveItems([cartItem]);
        // Dropdown is cleared when modal is closed/cancelled via clearDropdownSelection
        return;
      }

      // Clear dropdown selection for other actions
      setDropdownSelections((prev) => {
        if (!cartItem.uid || !(cartItem.uid in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[cartItem.uid];
        return next;
      });
    },
    [handleRemoveItems]
  );

  return {
    dropdownSelections,
    handleItemDropdownChange,
    handleDismissRemoveBanner,
    clearDropdownSelection,
  };
};
